import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import type { RosterMember } from "./types";

// What each dot in the gutter means, said once in the header rather than
// learned by guessing. Colour alone is never the signal: the count beside the
// swatch is the same fact in words, and each dot carries the sentence on hover.
export const MARK = {
  mine: { dot: "bg-accent-teal-bold", why: "Changed by this request." },
  gone: {
    dot: "bg-accent-red-bold",
    why: "Leaving the roster. They stop being paid once this is published.",
  },
  staged: {
    dot: "bg-neutral",
    why: "Already waiting on your roster before this request. Publishing this proposal publishes it too.",
  },
} as const;

type MarkKind = keyof typeof MARK;

/**
 * Which dot a row gets: what kind of change it is first, then whose.
 *
 * Someone leaving is red whoever staged it. This used to read whose change it
 * was first, which made red mean "leaving because of this request" , and the
 * editing agent has no way to remove anyone, so that was unreachable by the
 * only route the sentence described. The one way to reach it was for the agent
 * to change a field on a person somebody else had dropped, where the sentence
 * would then have been a lie. A person coming off the roster stops being paid,
 * which is the largest thing this table can say, so it is said regardless of
 * who did it.
 */
export const markOf = (member: RosterMember): MarkKind | null => {
  if (!member.change) return null;
  if (member.change === "removed") return "gone";
  return member.from_request ? "mine" : "staged";
};

export const Legend = ({
  kind,
  children,
}: {
  kind: MarkKind;
  children: string;
}) => (
  <Tooltip>
    <TooltipTrigger
      render={<span className="flex items-center gap-1.5 text-subtle" />}
    >
      <span
        aria-hidden
        className={cn("h-1.5 w-1.5 rounded-full", MARK[kind].dot)}
      />
      {children}
    </TooltipTrigger>
    <TooltipContent>{MARK[kind].why}</TooltipContent>
  </Tooltip>
);
